import { isDefined } from 'src/app/@utils/is-defined';

import { CxsBasket, CxsBasketLine } from '@accelerator/basket';
import { BasketCalculatedLineViewModel as Line, BasketLineType } from '@models/api-orders.models';

import { Basket, BasketLine } from './basket.model';

export const cxsBasketToKKBasket = (cxsBasket: CxsBasket): Basket => ({
    ...cxsBasket,
    lines: cxsBasketLinesToKKBasketLines(cxsBasket.lines)
});

export type LineWithQuantities = Line & { initialQuantity?: number };

const cxsBasketLinesToKKBasketLines = (cxsBasketLines: CxsBasketLine[]): BasketLine[] => {
    const basketLines: BasketLine[] = cxsBasketLines.map(cxsLine => ({ ...cxsLine }));
    cxsBasketLines.forEach(cxsLine => {
        cxsLine.relatedFreeProducts?.forEach(freeProductVariantNo => {
            const actualProductLine = basketLines.find(({ variantNo }) => variantNo === freeProductVariantNo);
            if (actualProductLine) {
                const referenceLine: BasketLine = actualProductLine;
                const { quantityThatIsFree } = referenceLine;
                basketLines.push({
                    ...actualProductLine,
                    variantNo: getFreeProductVariantNo(cxsLine.variantNo, freeProductVariantNo),
                    parentProductVariantNo: cxsLine.variantNo,
                    lineType: 'CalculatedFreeProduct',
                    giftWrappings: [],
                    relatedFreeProducts: undefined,
                    quantity: quantityThatIsFree,
                    cannotRemove: true
                });
                referenceLine.actualQuantity = referenceLine.quantity;
                referenceLine.quantity -= quantityThatIsFree;
                referenceLine.actualQuantityThatIsFree = quantityThatIsFree;
                referenceLine.quantityThatIsFree = 0;
                referenceLine.cannotRemove = true;
                referenceLine.isRelatedFreeProduct = true;
                if (referenceLine.quantity === 0) {
                    referenceLine.lineType = 'CalculatedReferenceLine';
                }
            }
        });
    });
    return basketLines;
};

export const getFreeProductVariantNo = (parentProductVariantNo: string, realLineVariantNo: string): string =>
    `${parentProductVariantNo}|${realLineVariantNo}`;

export const kkBasketToCxsBasket = (basket: Basket): CxsBasket => ({
    ...basket,
    lines: kkBasketLinesToCxsBasketLines(basket.lines)
});

const kkBasketLinesToCxsBasketLines = (lines: BasketLine[]): CxsBasketLine[] => {
    const cxsBasketLines: CxsBasketLine[] = [];
    lines.forEach(line => {
        if (
            line.lineType === 'GiftWrapping' ||
            line.lineType === 'Normal' ||
            line.lineType === 'OutOfStockRange' ||
            line.lineType === 'CalculatedReferenceLine'
        ) {
            const lineType = line.lineType === 'CalculatedReferenceLine' ? 'Normal' : line.lineType;
            const cxsBasketLine = {
                ...line,
                lineType,
                quantity: line.actualQuantity || line.quantity,
                quantityThatIsFree: line.actualQuantityThatIsFree || line.quantityThatIsFree
            };
            delete cxsBasketLine.actualQuantity;
            delete cxsBasketLine.actualQuantityThatIsFree;
            delete cxsBasketLine.cannotRemove;
            delete cxsBasketLine.isRelatedFreeProduct;
            cxsBasketLines.push(cxsBasketLine);
        }
    });
    return cxsBasketLines;
};

export type BasketEvent = { action: 'add' | 'remove' | 'other' | 'error'; lines: LineWithQuantities[] };

export const mapToAddRemoveEvent = (prev: Line[], curr: Line[]): BasketEvent => {
    // Find out if there is a diff between the lines
    const diff = getDiffLines(prev, curr);

    if (diff.length) {
        const isAdd = curr.length > prev.length;
        return { action: isAdd ? 'add' : 'remove', lines: diff };
    }

    // If no difference between the lines, quantity must have changed
    // on one find that
    const changedLine = getQuantityDiffLine(prev, curr);

    const previousQuantity = prev.find(line => line.variantNo === changedLine?.variantNo)?.quantity;

    const currentQuantity = changedLine?.quantity;

    // Add the correct add or remove quantity to those lines
    if (isDefined(changedLine) && isDefined(previousQuantity) && isDefined(currentQuantity)) {
        const isChangedAdd = currentQuantity > previousQuantity;
        const quantityDiff = Math.abs(previousQuantity - currentQuantity);

        return {
            action: isChangedAdd ? 'add' : 'remove',
            lines: [{ ...changedLine, quantity: quantityDiff, initialQuantity: currentQuantity }]
        };
    }

    return { action: 'other', lines: [] };
};

/**
 * Withdraw line if there is a quantity difference, from previous and current lines
 * @param prev
 * @param curr
 */
const getQuantityDiffLine = (prev: Line[], curr: Line[]): Line =>
    curr.filter(line =>
        prev.some(prevLine => prevLine.variantNo === line.variantNo && prevLine.quantity !== line.quantity)
    )[0] as Line;

/**
 * Withdraw array of different lines between previous and current lines
 * @param prev
 * @param curr
 */
const getDiffLines = (prev: Line[], curr: Line[]): Line[] =>
    curr
        .filter(line => !prev.some(prevLine => line.variantNo === prevLine.variantNo))
        .concat(
            prev.filter(concatPrevLine => !curr.some(concatLine => concatPrevLine.variantNo === concatLine.variantNo))
        );

/**
 * Filter array of lines based by basket line type
 * @param lines
 * @param lineType
 */
export const filterLines = (lines: Line[], lineType: BasketLineType): Line[] =>
    lines.filter(line => line.lineType === lineType);

/**
 * Map to line with an index if more than 1 product line
 * @param lines
 * @param lineType
 */
export const mapWithIndex = (lines: Line[]): (Line & { index?: number })[] => {
    if (lines.length > 1) {
        return lines.map((line, index) => ({ ...line, index: index + 1 }));
    }

    return lines;
};
